import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle, 
  Download, Users, AlertTriangle, Tag
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

const UploadLeadsPage = () => {
  const { selectedCampaign, selectedBusiness, user } = useAuth();
  const businessId = selectedBusiness?.business_id || user?.business_id;
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [tags, setTags] = useState([]);
  const [selectedTagId, setSelectedTagId] = useState('');

  const authHeaders = (includeContentType = false) => {
    const token = localStorage.getItem('token');
    const h = token ? { Authorization: `Bearer ${token}` } : {};
    if (includeContentType) h['Content-Type'] = 'application/json';
    return h;
  };

  const fetchTags = useCallback(async () => {
    if (!businessId) return;
    const res = await fetch(`${API_URL}/api/tags?business_id=${businessId}`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (res.ok) setTags(await res.json());
  }, [businessId]);

  useEffect(() => { fetchTags(); }, [fetchTags]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedCampaign) {
      toast.error('Please select a file and campaign');
      return;
    }

    setIsUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(
        `${API_URL}/api/leads/upload?campaign_id=${selectedCampaign.campaign_id}&skip_duplicates=${skipDuplicates}${selectedTagId ? `&tag_id=${selectedTagId}` : ''}`,
        {
          method: 'POST',
          headers: authHeaders(),
          credentials: 'include',
          body: formData
        }
      );

      if (response.ok) {
        const result = await response.json();
        setUploadResult(result);
        
        if (result.imported > 0) {
          toast.success(`Successfully imported ${result.imported} leads!`);
        }
        if (result.duplicates_found > 0) {
          toast.info(`Found ${result.duplicates_found} duplicates${skipDuplicates ? ' (skipped)' : ''}`);
        }
        if (result.errors.length > 0) {
          toast.warning(`${result.errors.length} rows had errors`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = "name,email,phone,notes\nJohn Smith,john@email.com,+44 7700 900001,Interested in training\nJane Doe,jane@email.com,+44 7700 900002,Called last week";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  if (!selectedCampaign) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">No Campaign Selected</h2>
          <p className="text-slate-500 mt-2">Please select a campaign from the sidebar</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="upload-leads-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Upload Leads
        </h1>
        <p className="text-slate-500 mt-1">Import leads from a CSV file into {selectedCampaign.name}</p>
      </div>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-cyan-500" />
            CSV Format
          </CardTitle>
          <CardDescription>
            Your CSV file should have these columns (header row required)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge variant="secondary">name *</Badge>
            <Badge variant="secondary">email</Badge>
            <Badge variant="secondary">phone</Badge>
            <Badge variant="secondary">notes</Badge>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Uploaded leads will be tagged as "uploaded" source and placed in the New stage. 
            The system will check for duplicates based on email or phone number.
          </p>
          <Button variant="outline" size="sm" onClick={downloadTemplate}>
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>
        </CardContent>
      </Card>

      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-cyan-500" />
            Upload File
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* File Drop Zone */}
          <div 
            className={`
              border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
              ${selectedFile ? 'border-cyan-300 bg-cyan-50' : 'border-slate-200 hover:border-cyan-300 hover:bg-slate-50'}
            `}
            onClick={() => fileInputRef.current?.click()}
            data-testid="file-drop-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            {selectedFile ? (
              <div className="space-y-2">
                <FileSpreadsheet className="w-12 h-12 text-cyan-500 mx-auto" />
                <p className="font-medium text-slate-900">{selectedFile.name}</p>
                <p className="text-sm text-slate-500">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setUploadResult(null); }}>
                  Change file
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-12 h-12 text-slate-300 mx-auto" />
                <p className="font-medium text-slate-700">Click to select a CSV file</p>
                <p className="text-sm text-slate-500">or drag and drop</p>
              </div>
            )}
          </div>

          {/* Options */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label htmlFor="skip-duplicates" className="font-medium">Skip Duplicates</Label>
              <p className="text-sm text-slate-500">Don't import leads that already exist</p>
            </div>
            <Switch
              id="skip-duplicates"
              checked={skipDuplicates}
              onCheckedChange={setSkipDuplicates}
              data-testid="skip-duplicates-toggle"
            />
          </div>

          {/* Tag for this upload */}
          <div className="space-y-2">
            <Label className="font-medium flex items-center gap-2">
              <Tag className="h-4 w-4 text-slate-400" />
              Tag This Upload (Optional)
            </Label>
            <p className="text-sm text-slate-500">All imported leads will be tagged — useful for bulk workflow enrolment later.</p>
            <Select value={selectedTagId || 'none'} onValueChange={v => setSelectedTagId(v === 'none' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="No tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No tag</SelectItem>
                {tags.map(t => (
                  <SelectItem key={t.tag_id} value={t.tag_id}>
                    <span className="flex items-center gap-2">
                      <span className="inline-block h-2 w-2 rounded-full" style={{ background: t.color }} />
                      {t.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTagId && (
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                Every imported lead will receive the <strong>{tags.find(t => t.tag_id === selectedTagId)?.name}</strong> tag. You can then go to Leads → select all → Add to Workflow.
              </div>
            )}
          </div>

          {/* Upload Button */}
          <Button 
            onClick={handleUpload}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
            disabled={!selectedFile || isUploading}
            data-testid="upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {isUploading ? 'Uploading...' : 'Upload Leads'}
          </Button>
        </CardContent>
      </Card>

      {/* Upload Results */}
      {uploadResult && (
        <Card className="animate-fade-in" data-testid="upload-results">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {uploadResult.imported > 0 ? (
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              ) : (
                <AlertCircle className="w-5 h-5 text-amber-500" />
              )}
              Upload Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-slate-900">{uploadResult.total_rows}</p>
                <p className="text-sm text-slate-500">Total Rows</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-emerald-600">{uploadResult.imported}</p>
                <p className="text-sm text-emerald-700">Imported</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-amber-600">{uploadResult.duplicates_found}</p>
                <p className="text-sm text-amber-700">Duplicates</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-600">{uploadResult.errors.length}</p>
                <p className="text-sm text-red-700">Errors</p>
              </div>
            </div>

            {uploadResult.duplicates_found > 0 && (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  Found {uploadResult.duplicates_found} duplicate leads based on email/phone. 
                  {uploadResult.duplicates_skipped > 0 && ` ${uploadResult.duplicates_skipped} were skipped.`}
                </AlertDescription>
              </Alert>
            )}

            {uploadResult.errors.length > 0 && (
              <div>
                <p className="font-medium text-red-700 mb-2">Errors:</p>
                <div className="max-h-40 overflow-y-auto bg-red-50 rounded-lg p-3">
                  {uploadResult.errors.map((error, i) => (
                    <p key={i} className="text-sm text-red-600">{error}</p>
                  ))}
                </div>
              </div>
            )}

            {uploadResult.imported > 0 && (
              <Alert className="bg-emerald-50 border-emerald-200">
                <CheckCircle className="h-4 w-4 text-emerald-600" />
                <AlertDescription className="text-emerald-800">
                  <Users className="w-4 h-4 inline mr-1" />
                  {uploadResult.imported} leads have been added to the "New" stage with source tagged as "uploaded".
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UploadLeadsPage;
